// Carnivoro loyalty — Klaviyo client-side integration.
//
// Uses Klaviyo's public Client API (no private key needed).
// On "RESERVE MY BONUS":
//   1. Subscribes email to the Klaviyo list (double opt-in per Klaviyo settings)
//   2. Tracks a "Carnivoro Game Completed" event with score + steaks
//   3. Stores steaks_earned as a profile property for later loyalty credit
//
// When your loyalty program launches:
//   - Export the list from Klaviyo (email + steaks_earned)
//   - Bulk-import into your loyalty app and credit each person's STEAKS
//   - Or build a Klaviyo flow that auto-credits via loyalty app API

'use strict';

var scoring   = (typeof require !== 'undefined') ? require('./scoring.js')   : (typeof window !== 'undefined' ? window.CarnivoroScoring : null);
var validator = (typeof require !== 'undefined') ? require('./validator.js') : (typeof window !== 'undefined' ? window.CarnivoroValidator : null);

// ─── Klaviyo config ──────────────────────────────────────────────
var KLAVIYO_COMPANY_ID = 'XuhpfG';       // Carnivoro public API key
var KLAVIYO_LIST_ID    = 'YpjMti';       // Bullboy Game Signup list
var KLAVIYO_API        = 'https://a.klaviyo.com/client';
// ─────────────────────────────────────────────────────────────────

function tierFor(points) {
  return 'FORAGER'; // single tier for pre-launch
}

function submitGameResult(email, rawScore, gameData) {
  // Client-side validation (stops obvious tampering)
  if (validator) {
    var check = validator.validateGameResult(gameData);
    if (!check.ok) {
      return Promise.resolve({
        points: 0, tier_unlocked: null, verified: false, errors: check.errors
      });
    }
  }

  var points = scoring ? scoring.scoreToPoints(rawScore) : 0;
  var tier = tierFor(points);
  var discountTier = (gameData && gameData.discountTier) || 10; // 10 | 12 | 15
  var rankName     = (gameData && gameData.rankName) || '';
  var isNewPB      = !!(gameData && gameData.isNewPB);

  if (typeof console !== 'undefined') {
    console.log('[carnivoro] submitting to Klaviyo:', email, 'tier=' + discountTier + '%', 'score=' + rawScore);
  }

  // 1. Subscribe to list (creates profile if new)
  var subBody = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Cow Boy Game',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: email,
              properties: {
                game_score: rawScore,
                game_rank: (gameData && gameData.levelReached) || 0,
                game_rank_name: rankName,
                // Drives the Klaviyo flow's conditional split → 3 different welcome emails
                discount_tier: discountTier,
                is_new_pb: isNewPB,
                signup_source: 'cowboy_game'
              }
            }
          }
        }
      },
      relationships: {
        list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
      }
    }
  };

  var subPromise = fetch(KLAVIYO_API + '/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15'
    },
    body: JSON.stringify(subBody)
  });

  // 2. Track "Carnivoro Game Completed" event
  var eventBody = {
    data: {
      type: 'event',
      attributes: {
        metric: { data: { type: 'metric', attributes: { name: 'Carnivoro Game Completed' } } },
        profile: { data: { type: 'profile', attributes: { email: email } } },
        properties: {
          score: rawScore,
          rank: (gameData && gameData.levelReached) || 0,
          rank_name: rankName,
          discount_tier: discountTier,
          is_new_pb: isNewPB,
          items_caught: (gameData && gameData.itemsCaught) || 0,
          duration_seconds: (gameData && gameData.durationMs) ? Math.round(gameData.durationMs / 1000) : 0
        },
        time: new Date().toISOString()
      }
    }
  };

  var eventPromise = fetch(KLAVIYO_API + '/events/?company_id=' + KLAVIYO_COMPANY_ID, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15'
    },
    body: JSON.stringify(eventBody)
  });

  // Fire both in parallel, resolve once both finish
  return Promise.all([subPromise, eventPromise]).then(function (results) {
    var subOk = results[0].ok || results[0].status === 202;
    var evtOk = results[1].ok || results[1].status === 202;
    return { points: points, tier_unlocked: tier, verified: subOk && evtOk };
  }).catch(function (err) {
    if (typeof console !== 'undefined') console.warn('[carnivoro] Klaviyo error:', err);
    return { points: points, tier_unlocked: tier, verified: false };
  });
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { submitGameResult: submitGameResult, tierFor: tierFor };
} else if (typeof window !== 'undefined') {
  window.CarnivoroSubmit = { submitGameResult: submitGameResult, tierFor: tierFor };
}
